import { mwGet, mwPost, requireId, mapResponse } from "./client";
import { getReadScope, readCache } from "../../utils/readCache";

const mapFilter = (data, profileId) => {
  const legacy = data && !Object.prototype.hasOwnProperty.call(data, "OnlyShowClaimedItemsLocked");
  if (
    !(data?.ProfileUId === profileId || (legacy && data.ProfileUId == null)) ||
    typeof data.OnlyShowClaimedItems !== "boolean" ||
    (Object.prototype.hasOwnProperty.call(data, "OnlyShowClaimedItemsLocked") &&
      typeof data.OnlyShowClaimedItemsLocked !== "boolean")
  ) {
    throw new Error("Invalid MW content filter preferences");
  }
  // MW ProfileService inverts this field for Web (3EF75): true means SHOW
  // content outside packages on the wire, unlike the stored canonical value.
  return {
    profileId,
    showUnentitled: data.OnlyShowClaimedItems,
    // Older MW DTOs predate central rules and omit the lock field entirely.
    // That service also leaves ProfileUId null, although it checks the requested
    // profile's owner. Use the request identity only for this legacy response.
    // An explicit lock remains authoritative; malformed values are rejected above.
    locked: data.OnlyShowClaimedItemsLocked === true
  };
};

export async function getContentFilter(profileId, options) {
  const response = await mwGet(
    "/profiles/preferences",
    {
      ProfileUId: requireId(profileId)
    },
    options
  );
  const mapped = mapResponse(response, (data) => mapFilter(data, profileId));
  // A central rule can change outside this client. Do not reuse earlier lists.
  readCache.clear();
  return mapped;
}

export async function setContentFilter(
  profileId,
  showUnentitled,
  isCurrent = () => true
) {
  if (typeof showUnentitled !== "boolean")
    throw new Error("Invalid filter value");
  const scope = getReadScope();
  const ensureCurrent = () => {
    if (!isCurrent() || scope !== getReadScope())
      throw Object.assign(new Error("Profile context changed"), {
        code: "STALE_CONTEXT"
      });
  };
  ensureCurrent();
  // Re-read the effective rule immediately before saving. MW also enforces a
  // rule activated between this check and the write and returns its decision.
  const current = await getContentFilter(profileId);
  ensureCurrent();
  if (current.data.locked || current.data.showUnentitled === showUnentitled)
    return current;
  try {
    const response = await mwPost("/profiles/update-preferences", {
      ProfileUId: profileId,
      OnlyShowClaimedItems: showUnentitled
    });
    ensureCurrent();
    return mapResponse(response, (data) => mapFilter(data, profileId));
  } finally {
    // A transport failure may follow a committed update; invalidate either way.
    readCache.clear();
  }
}
